import { CartAddEvent } from '@theme/events';

const dialog = document.querySelector('[data-wallet-case-upsell]');
const configElement = document.getElementById('WalletCaseUpsellConfig');

if (dialog instanceof HTMLElement && configElement) {
  const details = dialog.querySelector('details');
  const config = JSON.parse(configElement.textContent || '{}');
  const brandPicker = dialog.querySelector('[data-wallet-upsell-brand]');
  const modelPicker = dialog.querySelector('[data-wallet-upsell-model]');
  const colorPicker = dialog.querySelector('[data-wallet-upsell-color]');
  const addButton = dialog.querySelector('[data-wallet-upsell-add]');
  const message = dialog.querySelector('[data-wallet-upsell-message]');
  const offerImage = dialog.querySelector('[data-wallet-upsell-image]');
  const offerTitle = dialog.querySelector('[data-wallet-upsell-title]');
  const comparePrice = dialog.querySelector('[data-wallet-upsell-compare-price]');
  const discountedPrice = dialog.querySelector('[data-wallet-upsell-price]');
  const closeButtons = dialog.querySelectorAll('[data-wallet-upsell-close]');

  const formatter = new Intl.NumberFormat(config.locale || 'en-US', {
    style: 'currency',
    currency: config.currency || 'USD',
  });

  const money = (cents) => formatter.format(Number(cents || 0) / 100);
  const normalize = (value) => String(value || '').trim();
  const unique = (values) => [...new Set(values.filter(Boolean))];

  const splitCombinedOption = (value) => {
    const parts = normalize(value)
      .split(/\s*\/\s*/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length >= 2) {
      return { color: parts[0], model: parts.slice(1).join(' / ') };
    }

    return { color: 'Standard', model: parts[0] || 'Phone model' };
  };

  const buildCombinations = (offer) => {
    const optionNames = Array.isArray(offer?.optionNames) ? offer.optionNames : [];
    const colorIndex = optionNames.findIndex((name) => /color|colour/i.test(name));
    const modelIndex = optionNames.findIndex((name) => /model|phone|device/i.test(name));

    return (Array.isArray(offer?.variants) ? offer.variants : []).map((variant) => {
      const options = Array.isArray(variant.options) ? variant.options : [];
      let color;
      let model;

      if (colorIndex >= 0 && modelIndex >= 0 && colorIndex !== modelIndex) {
        color = normalize(options[colorIndex]);
        model = normalize(options[modelIndex]);
      } else {
        ({ color, model } = splitCombinedOption(options[0]));
      }

      return { ...variant, color, model };
    });
  };

  let currentOffer = null;
  let combinations = [];
  let selectedVariant = null;
  let adding = false;

  const setOptions = (select, values, placeholder) => {
    select.replaceChildren();

    const prompt = document.createElement('option');
    prompt.value = '';
    prompt.textContent = placeholder;
    select.append(prompt);

    values.forEach((value) => {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.append(option);
    });
  };

  const showOfferDetails = (offer, variant) => {
    if (!offer) return;

    offerTitle.textContent = offer.title || 'Magnetic phone case';
    offerTitle.href = offer.url || '#';

    const displayVariant = variant || combinations.find((item) => item.available) || combinations[0];
    if (displayVariant) {
      comparePrice.textContent = money(displayVariant.price);
      discountedPrice.textContent = money(Math.max(0, displayVariant.price - config.discountCents));
    }

    const image = displayVariant?.image || offer.image;
    if (image) {
      offerImage.src = image;
      offerImage.alt = offer.title || 'Magnetic phone case';
    }
  };

  const updateButton = () => {
    const ready = Boolean(currentOffer && selectedVariant && !adding);
    addButton.disabled = !ready;
    brandPicker.disabled = adding;
    modelPicker.disabled = adding || !combinations.some((variant) => variant.available);
    colorPicker.disabled = adding || !modelPicker.value || !combinations.some(
      (variant) => variant.available && variant.model === modelPicker.value
    );

    if (!brandPicker.value) {
      message.textContent = 'Choose your phone brand.';
    } else if (!modelPicker.value) {
      message.textContent = 'Choose your phone model.';
    } else if (!colorPicker.value) {
      message.textContent = 'Choose a color.';
    } else if (!selectedVariant) {
      message.textContent = 'That combination is currently unavailable.';
    } else {
      message.textContent = '';
    }
  };

  const updateVariant = () => {
    selectedVariant = combinations.find(
      (variant) =>
        variant.available && variant.model === modelPicker.value && variant.color === colorPicker.value
    );

    showOfferDetails(currentOffer, selectedVariant);
    updateButton();
  };

  const updateColors = () => {
    selectedVariant = null;
    const colors = unique(
      combinations
        .filter((variant) => variant.available && variant.model === modelPicker.value)
        .map((variant) => variant.color)
    );

    setOptions(colorPicker, colors, modelPicker.value ? 'Choose color' : 'Choose model first');
    colorPicker.disabled = !modelPicker.value || colors.length === 0;

    if (colors.length === 1) {
      colorPicker.value = colors[0];
    }

    updateVariant();
  };

  const updateBrand = () => {
    currentOffer = config.offers?.[brandPicker.value] || null;
    combinations = buildCombinations(currentOffer);
    selectedVariant = null;

    const models = unique(combinations.filter((variant) => variant.available).map((variant) => variant.model));
    setOptions(modelPicker, models, currentOffer ? 'Choose phone model' : 'Choose phone brand first');
    modelPicker.disabled = !currentOffer || models.length === 0;
    setOptions(colorPicker, [], 'Choose model first');
    colorPicker.disabled = true;

    showOfferDetails(currentOffer);
    updateButton();
  };

  const reset = () => {
    brandPicker.value = config.defaultBrand || 'samsung';
    adding = false;
    addButton.textContent = 'Add case';
    message.textContent = '';
    updateBrand();
  };

  let eligible = false;
  let syncVersion = 0;

  // Keep the same offer node and its selections when the theme replaces cart HTML.
  const mountOffer = () => {
    const host = document.querySelector('cart-drawer-component .cart-drawer__items');
    if (eligible && host && dialog.parentElement !== host) host.append(dialog);
    dialog.hidden = !eligible || !host;
  };

  const applyCart = (items) => {
    const offerProductIds = Object.values(config.offers || {}).map((offer) => String(offer.productId));
    const hasWallet = items.some((item) => String(item.product_id) === String(config.triggerProductId));
    const hasCase = items.some((item) => offerProductIds.includes(String(item.product_id)));
    eligible = hasWallet && !hasCase;
    if (!eligible) details.open = false;
    mountOffer();
  };

  const syncCart = async () => {
    const version = ++syncVersion;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;

      const cart = await response.json();
      if (version === syncVersion) applyCart(cart.items || []);
    } catch (error) {
      console.error('Unable to update the wallet cart offer.', error);
    }
  };

  const closeOffer = () => {
    details.open = false;
    dialog.querySelector('summary').focus();
  };

  const addCase = async () => {
    if (!currentOffer || !selectedVariant || adding) {
      updateButton();
      return;
    }

    adding = true;
    updateButton();
    addButton.textContent = 'Adding…';
    message.textContent = '';

    const sectionIds = unique(
      [...document.querySelectorAll('cart-items-component[data-section-id]')].map(
        (component) => component.dataset.sectionId
      )
    );

    const payload = {
      items: [{ id: Number(selectedVariant.id), quantity: 1 }],
    };

    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || result.message || 'The case could not be added.');
      }

      eligible = false;
      details.open = false;
      mountOffer();
      adding = false;
      addButton.textContent = 'Add case';
      updateButton();
      document.dispatchEvent(
        new CartAddEvent(result, String(selectedVariant.id), {
          source: 'wallet-case-upsell',
          itemCount: 1,
          productId: String(currentOffer.productId),
          variantId: String(selectedVariant.id),
          sections: result.sections,
        })
      );
    } catch (error) {
      adding = false;
      addButton.textContent = 'Add case';
      updateButton();
      message.textContent = error.message || 'The case could not be added. Please try again.';
    }
  };

  document.addEventListener(CartAddEvent.eventName, (event) => {
    const data = event.detail?.data;
    if (!data?.didError) syncCart();
  });

  brandPicker.addEventListener('change', updateBrand);
  modelPicker.addEventListener('change', updateColors);
  colorPicker.addEventListener('change', updateVariant);
  addButton.addEventListener('click', addCase);
  closeButtons.forEach((button) => button.addEventListener('click', closeOffer));

  reset();
  applyCart(config.cartItems || []);
  new MutationObserver(mountOffer).observe(document.body, { childList: true, subtree: true });
}
